import { Link } from 'react-router-dom';
import { AlertCircle, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { formatTemplateName } from '@/lib/templateName';
import { unmappedVariablesOf, needsMappingReview } from '@/lib/templateVariables';
import {
  CUSTOM_MEDIA,
  MEDIA_ACCEPT,
  type CampaignLaunchState,
  type WhatsAppTemplateOption,
} from '@/hooks/useCampaignLaunchState';

// The pieces of a campaign's message that are chosen on different steps: the template and its
// values on the channel step, the header media and the SMS override on the media step. They are
// presentational only; the state lives in useCampaignLaunchState.

/** SMS body override. Its default is the chosen media's generated text, hence the media step. */
export function SmsMessageOverride({ state }: { state: CampaignLaunchState }) {
  const { customMessage, setCustomMessage, defaultMessage } = state;
  return (
    <div className="space-y-2">
      <Label htmlFor="launch-sms-message">Message (optional override)</Label>
      <Textarea
        id="launch-sms-message"
        placeholder={defaultMessage || 'Enter custom message...'}
        value={customMessage}
        onChange={(e) => setCustomMessage(e.target.value)}
        rows={4}
        className="resize-none"
        dir="auto"
      />
      {defaultMessage && !customMessage && (
        <p className="text-xs text-muted-foreground">
          Using generated campaign content if left empty
        </p>
      )}
    </div>
  );
}

function TemplateVariableNotice({ template, workspaceId }: { template: WhatsAppTemplateOption | null; workspaceId: string }) {
  const unmapped = unmappedVariablesOf(template);
  const labels = template?.metadata?.variableLabels || {};
  const describe = (indices: string[]) => indices
    .map((i) => (labels[i] ? `{{${i}}} (${labels[i]})` : `{{${i}}}`))
    .join(', ');

  // Ordered under the unmapped check, matching the server: when both apply the
  // more concrete problem is the one worth naming.
  if (unmapped.length === 0 && needsMappingReview(template)) {
    const review = template!.metadata!.mappingReview!;
    return (
      <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 mt-2 text-amber-900">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="text-xs space-y-1">
          <p className="font-medium">Variable mapping needs confirming</p>
          <p>
            {'This template was changed at the provider on '}
            {new Date(review.since).toLocaleDateString()}
            {review.changedIndices.length > 0
              ? `, and ${describe(review.changedIndices)} may no longer mean what `
                + `${review.changedIndices.length === 1 ? 'it did' : 'they did'} when the `
                + 'mapping was saved. '
              : ', and the wording around its variables changed since the mapping '
                + 'was saved. '}
            Sending now could deliver the wrong personal detail to every recipient.
          </p>
          <Link
            to={'/w/' + workspaceId + '/whatsapp-templates'}
            className="inline-flex items-center gap-1 underline hover:text-amber-950"
          >
            <ExternalLink className="h-3 w-3" />
            Check and confirm the mapping
          </Link>
        </div>
      </div>
    );
  }

  if (unmapped.length === 0) {
    return Object.keys(template?.variableMapping || {}).length > 0 ? (
      <p className="text-xs text-muted-foreground mt-2">
        Variables will be personalized per contact
      </p>
    ) : null;
  }
  return (
    <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 mt-2 text-amber-900">
      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="text-xs space-y-1">
        <p className="font-medium">
          {unmapped.length} variable{unmapped.length === 1 ? '' : 's'} not mapped
          to a contact field
        </p>
        <p>
          {describe(unmapped)}
          {' — WhatsApp rejects a message whose variables are not all filled in, '}
          so this campaign cannot be launched yet.
        </p>
        <Link
          to={'/w/' + workspaceId + '/whatsapp-templates'}
          className="inline-flex items-center gap-1 underline hover:text-amber-950"
        >
          <ExternalLink className="h-3 w-3" />
          Set the variable mapping
        </Link>
      </div>
    </div>
  );
}

/**
 * The WhatsApp template and everything it needs apart from its header media: the picker, what is
 * wrong with the chosen template, the header's {{1}} value and any dynamic button links.
 */
export function WhatsAppTemplatePicker({ state }: { state: CampaignLaunchState }) {
  const {
    workspaceId, canSyncTemplates, whatsappTemplates, whatsappTemplateId, chooseTemplate,
    selectedTemplate, selectedTemplateListed, selectedTemplateMissing, loadingTemplates,
    templatesError, syncingTemplates, templatesSyncIssue, syncTemplates, requirements,
    loadingRequirements, dynamicButtons, headerText, setHeaderText, buttonValues, setButtonValues,
  } = state;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="font-medium">WhatsApp Template</Label>
        {canSyncTemplates && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() => syncTemplates()}
            disabled={syncingTemplates || loadingTemplates}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncingTemplates ? 'animate-spin' : ''}`} />
            {syncingTemplates ? 'Syncing…' : 'Sync'}
          </Button>
        )}
      </div>
      {!loadingTemplates && !templatesError && whatsappTemplates.length > 0 && syncingTemplates && (
        <p className="text-xs text-muted-foreground">Checking WhatsApp for template updates…</p>
      )}
      {/* The server's message already says the list shown is the last known one. */}
      {!loadingTemplates && !templatesError && !syncingTemplates && templatesSyncIssue && (
        <p className="text-xs text-amber-700">{templatesSyncIssue}</p>
      )}
      {loadingTemplates || (syncingTemplates && whatsappTemplates.length === 0 && !templatesError) ? (
        // An empty mirror mid-sync is not "no templates" yet - a first sync is often what fills it.
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading templates...
        </div>
      ) : templatesError ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium text-sm">Couldn&apos;t load your templates</span>
          </div>
          <p className="text-xs text-muted-foreground">
            The template list could not be fetched, so this is not a statement about what you
            have. Reload the page to try again — a sync will not help.
          </p>
        </div>
      ) : whatsappTemplates.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="h-4 w-4" />
            <span className="font-medium text-sm">No approved templates</span>
          </div>
          <p className="text-xs text-amber-600">
            WhatsApp campaigns need an approved message template. None are available for
            this workspace yet
            {canSyncTemplates
              ? ' — press Sync above to pull the latest approved list, or create one in Templates.'
              : ' — ask a workspace admin to sync or create one in Templates.'}
          </p>
          <Link to={'/w/' + workspaceId + '/whatsapp-templates'} className="inline-flex items-center gap-1 text-xs text-amber-700 underline hover:text-amber-900">
            <ExternalLink className="h-3 w-3" />
            Manage Templates
          </Link>
        </div>
      ) : (
        <>
          <Select value={whatsappTemplateId} onValueChange={chooseTemplate}>
            <SelectTrigger>
              <SelectValue placeholder="Select a template..." />
            </SelectTrigger>
            <SelectContent>
              {whatsappTemplates.map((tpl) => (
                <SelectItem key={tpl.id} value={tpl.id}>
                  {formatTemplateName(tpl.name)} ({tpl.language}) - {tpl.category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTemplateMissing && (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p className="text-xs">
                The template selected earlier is no longer approved or was removed on WhatsApp.
                Choose another to launch this campaign.
              </p>
            </div>
          )}
          {whatsappTemplateId && selectedTemplateListed && (
            <div className="rounded-lg bg-muted/50 p-3 space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">Template body</Badge>
              </div>
              <p className="text-sm whitespace-pre-wrap" dir="auto">
                {selectedTemplate?.body}
              </p>
              <TemplateVariableNotice template={selectedTemplate} workspaceId={workspaceId} />
            </div>
          )}

          {/* What this template needs supplied beyond its body variables.
              Campaigns had none of this: a template with a media header, a variable text
              header or a dynamic URL button launched with those components missing, and Meta
              rejected every message for a component mismatch — after the credits were
              reserved. The server refuses such a launch now; these fields are how it stops
              being a refusal and becomes a send. The header media itself is chosen on the
              media step, beside the creative it comes from. */}
          {whatsappTemplateId && loadingRequirements && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking what this template needs...
            </div>
          )}

          {requirements?.hasCarousel && (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p className="text-xs">
                This is a carousel template. Carousels are not supported yet, so this campaign
                cannot be launched — send it from the provider console for now.
              </p>
            </div>
          )}

          {requirements?.header?.hasVariable && (
            <div className="space-y-2">
              <Label htmlFor="launch-header-text" className="text-xs text-muted-foreground">
                Header value
              </Label>
              {requirements.header.text && (
                <p className="text-xs text-muted-foreground font-mono">{requirements.header.text}</p>
              )}
              <Input
                id="launch-header-text"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                placeholder="Fills the {{1}} in the header"
              />
              {/* One value for the whole campaign — the header is a property of the message,
                  not of the contact, so it is not personalised the way the body is. */}
              <p className="text-xs text-muted-foreground">
                The same value is used for every recipient.
              </p>
            </div>
          )}

          {dynamicButtons.map((button) => (
            <div key={button.index} className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                &quot;{button.text || 'URL'}&quot; button link value
              </Label>
              {button.url && (
                <p className="text-xs text-muted-foreground font-mono break-all">{button.url}</p>
              )}
              <Input
                value={buttonValues[String(button.index)] || ''}
                onChange={(e) =>
                  setButtonValues({ ...buttonValues, [String(button.index)]: e.target.value })
                }
                placeholder="Fills the {{1}} in the button's link"
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** The image, video or document for a template's media header. Rendered only when it has one. */
export function HeaderMediaFields({ state }: { state: CampaignLaunchState }) {
  const {
    headerFormat, campaignAssets, mediaChoice, chooseHeaderMedia, headerMediaUrl, setHeaderMediaUrl,
    headerMediaId, uploadedMediaName, uploadingMedia, handleMediaUpload, templateDefaultUid,
    loadingHeaderAssets, refreshHeaderPreviews,
  } = state;

  return (
    <div className="space-y-2 rounded-lg border p-4">
      <Label className="font-medium">
        Template header {headerFormat!.toLowerCase()}
      </Label>
      <p className="text-xs text-muted-foreground">
        The template you chose shows {headerFormat === 'IMAGE' ? 'an' : 'a'} {headerFormat!.toLowerCase()} above
        the message. Choose which one to send.
      </p>

      {/* The campaign's own generated media first. It was made by this product and is
          already in storage, so asking someone to download and re-upload their own
          image would be a pointless tax on every launch. */}
      {loadingHeaderAssets && campaignAssets.length === 0 ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading this media&apos;s {headerFormat!.toLowerCase()}s…
        </p>
      ) : campaignAssets.length > 0 ? (
        <Select value={mediaChoice} onValueChange={chooseHeaderMedia}>
          <SelectTrigger>
            <SelectValue placeholder="Choose the header media..." />
          </SelectTrigger>
          <SelectContent>
            {campaignAssets.map((asset) => (
              <SelectItem key={asset.uid} value={asset.uid}>
                {asset.label}
                {/* The template's own picture — the one WhatsApp reviewed — over the media's
                    selection, since that is the default this campaign started with. */}
                {asset.uid === templateDefaultUid
                  ? ' — the template’s default'
                  : asset.selected ? ' — selected for this campaign' : ''}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_MEDIA}>Upload a file or paste a link…</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-muted-foreground">
          This media has no generated {headerFormat!.toLowerCase()} to use, so
          choose a file or paste a link below.
        </p>
      )}

      {mediaChoice && mediaChoice !== CUSTOM_MEDIA && (() => {
        const chosen = campaignAssets.find(a => a.uid === mediaChoice);
        return chosen?.previewUrl ? (
          <img
            src={chosen.previewUrl}
            alt={chosen.label}
            className="h-24 w-auto rounded border object-cover"
            onError={refreshHeaderPreviews}
          />
        ) : null;
      })()}

      {(campaignAssets.length === 0 || mediaChoice === CUSTOM_MEDIA) && (
        <>
          <Input
            type="file"
            accept={MEDIA_ACCEPT[headerFormat!] || undefined}
            disabled={uploadingMedia}
            onChange={(e) => handleMediaUpload(e.target.files?.[0])}
          />

          {uploadingMedia && <p className="text-xs text-muted-foreground">Uploading…</p>}

          {headerMediaId && !uploadingMedia && (
            <p className="text-xs text-green-700">
              {uploadedMediaName} uploaded — it will be sent by reference, so WhatsApp
              does not have to fetch it.
            </p>
          )}

          {!headerMediaId && (
            <>
              <p className="text-xs text-muted-foreground">
                Or paste a public HTTPS URL. We download it and upload it for you, so
                it only has to be reachable right now, not when WhatsApp gets around
                to it.
              </p>
              <Input
                value={headerMediaUrl}
                onChange={(e) => setHeaderMediaUrl(e.target.value)}
                placeholder="https://..."
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
